import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Group, Modal, NumberInput, SegmentedControl, Stack, Text } from '@mantine/core';
import { formatMoney } from '@/lib/format';

interface BulkPriceDialogProps {
  readonly opened: boolean;
  /** Охват изменения — показываем явно (товар, базис, урожай, число цен), защита от случайностей. */
  readonly scope: string;
  /** Текущий диапазон моих цен — для предпросмотра результата; null — цен нет. */
  readonly range: { readonly min: number; readonly max: number } | null;
  /** Максимально допустимое уменьшение, ₽/т (чтобы цены не ушли ниже минимума). 0 — уменьшать нельзя. */
  readonly maxDiscount: number;
  readonly loading?: boolean;
  /** Знаковая дельта: + увеличение, − уменьшение. */
  readonly onApply: (delta: number) => void;
  readonly onClose: () => void;
}

const MAX_VALUE = 999_999;

const rangeText = (range: { readonly min: number; readonly max: number }): string =>
  range.min === range.max ? formatMoney(range.min) : `${formatMoney(range.min)}–${formatMoney(range.max)}`;

// Изменение всех цен прайс-листа: знак и сумма в ₽/т, применяется ко всем строкам качества и обеим предоплатам.
// Охват — обычной строкой, под полем — предпросмотр «было → станет». Сумма вне допустимого не обрезается молча:
// под полем ошибка. Пока запрос идёт, окно не закрывается — иначе результат пришёл бы без видимого окна.
export default function BulkPriceDialog({ opened, scope, range, maxDiscount, loading = false, onApply, onClose }: BulkPriceDialogProps) {
  const { t } = useTranslation();
  const [sign, setSign] = useState<'+' | '-'>('+');
  const [value, setValue] = useState<number | string>('');

  useEffect(() => {
    if (opened) {
      setSign('+');
      setValue('');
    }
  }, [opened]);

  const numeric = typeof value === 'number' ? value : Number(value);
  const entered = value !== '' && Number.isFinite(numeric);
  const maxForSign = sign === '-' ? Math.max(0, maxDiscount) : MAX_VALUE;
  const decreaseBlocked = sign === '-' && maxDiscount < 1;
  const tooBig = entered && numeric > maxForSign;
  const valid = entered && numeric >= 1 && !tooBig && !decreaseBlocked;
  const delta = sign === '+' ? numeric : -numeric;

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={t('app.buy.bulk.title')}
      centered
      closeOnEscape={!loading}
      closeOnClickOutside={!loading}
      withCloseButton={!loading}
    >
      <form
        onSubmit={(event) => {
          event.preventDefault();
          if (valid && !loading) {
            onApply(delta);
          }
        }}
      >
        <Stack gap="md">
          <Text size="sm">{t('app.buy.bulk.scope', { scope })}</Text>

          <SegmentedControl
            fullWidth
            value={sign}
            onChange={(next) => setSign(next as '+' | '-')}
            disabled={loading}
            data={[
              { value: '+', label: t('app.buy.bulk.increase') },
              { value: '-', label: t('app.buy.bulk.decrease') },
            ]}
          />

          {decreaseBlocked ? (
            <Text size="sm" c="loss.8" role="status">
              {t('app.buy.bulk.cantDecrease')}
            </Text>
          ) : (
            <NumberInput
              data-autofocus
              label={t('app.buy.bulk.amount')}
              description={sign === '-' ? t('app.buy.bulk.maxDiscount', { max: formatMoney(maxDiscount) }) : undefined}
              error={tooBig ? t('app.buy.bulk.tooBig', { max: formatMoney(maxForSign) }) : undefined}
              min={1}
              allowNegative={false}
              allowDecimal={false}
              clampBehavior="none"
              hideControls
              thousandSeparator=" "
              value={value}
              onChange={setValue}
              disabled={loading}
            />
          )}

          {range && valid ? (
            <Text size="sm" role="status">
              {t('app.buy.bulk.preview', {
                from: rangeText(range),
                to: rangeText({ min: range.min + delta, max: range.max + delta }),
              })}
            </Text>
          ) : null}

          <Group justify="flex-end" gap="sm">
            <Button variant="default" onClick={onClose} disabled={loading}>
              {t('common.cancel')}
            </Button>
            <Button type="submit" loading={loading} disabled={!valid}>
              {t('app.buy.bulk.apply')}
            </Button>
          </Group>
        </Stack>
      </form>
    </Modal>
  );
}
